/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import Contentful from '@svg/contentful';

import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentLoader } from '@common/hooks/useDocument';
import { ContentfulAuth } from './ContentfulAuth';
import { ContentfulContentTypes } from './ContentfulContentTypes';
import { ContentfulSchedule } from './ContentfulSchedule';
import { ContentfulFiles } from './ContentfulFiles';
import { LoaderLoading } from '../LoaderLoading';
import { useTheme } from '../../../../styles';
import { ContentfulLoaderCollection, LoaderConfiguration } from '@requests/loaders';
import { TelnyxBucket, TelnyxBucketFileEmbedding } from '@requests/buckets';

interface Props {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  open: string | null;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  documentLoader: DocumentLoader;
  loader: LoaderConfiguration | undefined;
}

export const ContentfulLoader: React.FC<Props> = ({ page, open, setOpen, setPage, loader, documentLoader }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  const { service_id, bucketName } = useParams();
  const { loading } = documentLoader;

  const bucketQueryKey = ['globalFiles', 'bucket', { service_id: service_id, bucket_name: bucketName }];
  const bucket = queryClient.getQueryData<TelnyxBucket>(bucketQueryKey);

  const embeddingQueryKey = ['globalFiles', 'embedding-files', { service_id, bucket_name: bucketName }];
  const embeddingFiles = queryClient.getQueryData<TelnyxBucketFileEmbedding[]>(embeddingQueryKey) || [];

  const [filteredDocuments, setFilteredDocuments] = useState(0);
  const [selectedCollections, setSelectedCollections] = useState<ContentfulLoaderCollection[]>([]);
  const [space, setSpace] = useState('');
  const [secretIdentifier, setSecretIdentifier] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const defaultValue = '0 12 * * *'; // every day at 12PM
  const [intervalValue, setIntervalValue] = useState<string>(loader?.refresh_interval_config.value || defaultValue);

  // unit is no longer used since we moved to react-js-cron
  const [unit, setUnit] = useState<string>(loader?.refresh_interval_config.unit || 'node-cron');

  const reset = () => {
    setOpen(null);
    setPage(0);
    setSecretIdentifier('');
    setSelectedCollections([]);
    setActiveStep(0);

    if (!loader) {
      setIntervalValue('');
      setUnit('node-cron');
    }
  };

  const defaultProps = { open, setOpen, page, setPage, reset };

  // complete the stepper when all the steps are done
  useEffect(() => {
    if (embeddingFiles.filter((file) => file.status === 'success').length === filteredDocuments && activeStep === 2) {
      setOpen(null);
    }
  }, [embeddingFiles, activeStep, filteredDocuments]);

  useEffect(() => {
    if (loader && !intervalValue) setIntervalValue(loader.refresh_interval_config.value);
    if (loader && !unit) setUnit(loader.refresh_interval_config.unit);
  }, [loader]);

  const isLoaderConfigured =
    Boolean(loader?.bucket_name) &&
    !loading &&
    embeddingFiles.length > 0 &&
    embeddingFiles.every((file) => file.status === 'success');

  const isLoaderConfiguredLoading = Boolean(loader?.bucket_name) && loading && embeddingFiles.length > 0;
  if (isLoaderConfiguredLoading) {
    return (
      <LoaderLoading
        header='Contentful'
        icon={<Contentful color={theme.text.standard} height={22} />}
        loading={isLoaderConfiguredLoading}
        setOpen={setOpen}
      />
    );
  }

  if (isLoaderConfigured) {
    const configuration = { intervalValue, setIntervalValue, unit, setUnit, loader: true };
    return <ContentfulSchedule {...defaultProps} configuration={configuration} />;
  }

  if (page === 0) {
    const configuration = { secretIdentifier, setSecretIdentifier, space, setSpace };
    return <ContentfulAuth {...defaultProps} configuration={configuration} />;
  }

  if (page === 1) {
    const configuration = { intervalValue, setIntervalValue, unit, setUnit, loader: Boolean(loader) };
    return <ContentfulSchedule {...defaultProps} configuration={configuration} />;
  }

  if (page === 2) {
    const configuration = {
      secretIdentifier,
      selected: selectedCollections,
      setSelected: setSelectedCollections,
      space,
    };
    return <ContentfulContentTypes {...defaultProps} configuration={configuration} />;
  }

  const isConfigurationReady =
    (!embeddingFiles.every((file) => file.status === 'success') || embeddingFiles.length === 0) && bucket;

  if (page === 3 && isConfigurationReady) {
    const configuration = {
      secretIdentifier,
      selected: selectedCollections,
      documentLoader: documentLoader,
      region: bucket?.region,
      refreshInterval: intervalValue,
      unit: unit,
      activeStep: activeStep,
      setActiveStep: setActiveStep,
      space,
      filteredDocuments,
      setFilteredDocuments,
    };

    return <ContentfulFiles {...defaultProps} configuration={configuration} />;
  }

  return null;
};
